import App from "./components/App";
import { AppContainer } from "react-hot-loader";
import { initializeIcons } from "@fluentui/font-icons-mdl2";
import { createTheme, registerIcons, ThemeProvider } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import * as ReactDOM from "react-dom";
import Auth from "./components/Auth";
import { ApolloProvider, ApolloClient, ApolloLink, InMemoryCache, HttpLink, gql } from "@apollo/client";
import { BrowserCacheLocation, Configuration, LogLevel, PublicClientApplication } from "@azure/msal-browser";
import { MixpanelProvider } from "react-mixpanel-browser";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import { MsalProvider } from "@azure/msal-react";
import { isNil } from "lodash";
import { IssueNewTokenDocument } from "@graphql";
import { getCurrentUrl } from "@shared";
import "./global-styles.css";
import { BrowserRouter, HashRouter } from "react-router-dom";
import PlusIcon from "assets/svg/plus-icon";
import CalendarIcon from "assets/svg/calendar-icon";
import ClockIcon from "assets/svg/clock-icon";

Sentry.init({
  release: "rs-outlook-addin-react@" + process.env.VERSION,
  environment: process.env.ENVIRONMENT,
  dsn: "https://5dd8ed0e804a43eb8c3b7cd163112174@o512102.ingest.sentry.io/5830159",
  integrations: [new BrowserTracing()],
  tracesSampleRate: 1.0,
});

/* global Office */
initializeIcons();

//Use custom icons for FluentUI
registerIcons({
  icons: {
    plusCustomIcon: <PlusIcon />,
    calendarIcon: <CalendarIcon />,
    clockIcon: <ClockIcon />,
  },
});

let isOfficeInitialized = false;

/* initialise GraphAPI call. */

export const graphAPI = () => {
  const url = getCurrentUrl();
  return url;
};

const httpLink = new HttpLink({ uri: `${graphAPI()}/graphql` });

const authLink = new ApolloLink((op, forward) => {
  // Retrieve the authorization token from local storage.
  const token = localStorage.getItem("token");
  const spaces_tenant = localStorage.getItem("tenant");
  const spaces_client = "624ad76a8c3e5a0d98237181";
  // Use the setContext method to set the HTTP headers.
  op.setContext({
    headers: {
      Authorization: token ? token : "",
      spaces_tenant: spaces_tenant ? spaces_tenant : "",
      spaces_client: spaces_client,
    },
  });

  return forward(op);
});

const refLink = new ApolloLink((op, forward) => {
  const token = localStorage.getItem("token");
  const spaces_tenant = localStorage.getItem("tenant");
  const spaces_client = "624ad76a8c3e5a0d98237181";
  op.setContext({
    headers: {
      Authorization: token ? token : "",
      spaces_tenant: spaces_tenant ? spaces_tenant : "",
      spaces_client: spaces_client,
    },
  });

  return forward(op);
});

const refreshClient = new ApolloClient({
  link: ApolloLink.from([refLink, httpLink]),
  cache: new InMemoryCache(),
});

const refreshTokenFn = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  const tenantId = localStorage.getItem("tenant");
  if (!isNil(refreshToken) && !isNil(tenantId)) {
    try {
      const refreshResolverResponse = await refreshClient.mutate({
        mutation: IssueNewTokenDocument,
        variables: {
          tenantId,
          refreshToken,
        },
      });
      const accessToken = refreshResolverResponse.data?.issueNewToken?.idToken;
      const newRefreshToken = refreshResolverResponse.data?.issueNewToken?.refreshToken;
      localStorage.setItem("token", accessToken);
      localStorage.setItem("refreshToken", newRefreshToken);
      return { accessToken, newRefreshToken };
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
};

const refreshLink = new ApolloLink((op, forward) => {
  refreshTokenFn();
  return forward(op);
});

export const authClient = new ApolloClient({
  link: ApolloLink.from([authLink, refreshLink, httpLink]),
  cache: new InMemoryCache(),
});

const isIE = window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1;
export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(logLevel, message);
}

// MSAL Configuration
const clientId = process.env.OUTLOOK_CLIENT_ID || "7073c8c8-2066-4608-8812-c8269c481023";
const configuration: Configuration = {
  auth: {
    clientId,
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage, // Needed to avoid "User login is required" error.
    storeAuthStateInCookie: isIE, // Recommended to avoid certain IE/Edge issues.
  },
  system: {
    loggerOptions: {
      loggerCallback,
      logLevel: LogLevel.Verbose,
      piiLoggingEnabled: false,
    },
  },
};
const pca = new PublicClientApplication(configuration);

// eslint-disable-next-line no-console
console.log(`Using ClientID: ${clientId} AND API: ${graphAPI}`);

const appTheme = createTheme({
  defaultFontStyle: { fontFamily: "museo-sans, sans-serif" },
});

const render = (Component) => {
  ReactDOM.render(
    <AppContainer>
      <MsalProvider instance={pca}>
        <ApolloProvider client={authClient}>
          <MixpanelProvider>
            <ThemeProvider theme={appTheme}>
              <Auth>
                <BrowserRouter>
                  <Component isOfficeInitialized={isOfficeInitialized} />
                </BrowserRouter>
              </Auth>
            </ThemeProvider>
          </MixpanelProvider>
        </ApolloProvider>
      </MsalProvider>
    </AppContainer>,
    document.getElementById("container"),
  );
};

/* Render application after Office initializes */
Office.onReady(() => {
  isOfficeInitialized = true;
  render(App);
});

if ((module as any).hot) {
  (module as any).hot.accept("./components/App", () => {
    const NextApp = require("./components/App").default;
    render(NextApp);
  });
}
